import * as SelectPrimitive from "@radix-ui/react-select";
import { X } from "lucide-react";
import * as React from "react";

import { SelectContext } from "./context";

export function SelectValue({
  placeholder: propPlaceholder,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>) {
  const ctx = React.useContext(SelectContext);

  // Simple Radix branch — delegate entirely to Radix
  if (ctx.isSimple) {
    return <SelectPrimitive.Value placeholder={propPlaceholder} {...props} />;
  }

  const {
    selectedValues,
    itemLabelsByValue,
    placeholder: contextPlaceholder,
    multiple,
    removeValue,
  } = ctx;
  const placeholder = propPlaceholder ?? contextPlaceholder;

  if (multiple) {
    if (selectedValues.length > 0) {
      return (
        <>
          {selectedValues.map((sv) => (
            <span
              key={sv}
              className="inline-flex max-w-full items-center gap-1 rounded-sm bg-gray-100 px-2 py-0.5 text-xs text-black"
            >
              <span className="truncate">
                {itemLabelsByValue.current.get(sv) ?? sv}
              </span>
              {/* biome-ignore lint/a11y/useSemanticElements: nested button markup inside the trigger causes invalid HTML and hydration issues */}
              <span
                role="button"
                tabIndex={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeValue(sv);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") {
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  removeValue(sv);
                }}
                className="cursor-pointer text-gray-500 hover:text-black"
                aria-label={`Remove ${itemLabelsByValue.current.get(sv) ?? sv}`}
              >
                <X className="size-3" />
              </span>
            </span>
          ))}
        </>
      );
    }

    return <span className="text-gray-600">{placeholder ?? "Select..."}</span>;
  }

  const selectedLabel =
    selectedValues[0] !== undefined
      ? itemLabelsByValue.current.get(selectedValues[0])
      : undefined;

  if (selectedLabel) {
    return <span className="truncate">{selectedLabel}</span>;
  }

  return <span className="text-gray-600">{placeholder ?? "Select..."}</span>;
}

SelectValue.displayName = SelectPrimitive.Value.displayName;
